import React from "react";
import { XMarkIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { TaskFormElement, TaskFormData } from "../../types";
import { useTranslation } from "react-i18next";

interface FormViewerProps {
  formData: TaskFormData;
  onClose?: () => void;
  title?: string;
}

export const FormViewer: React.FC<FormViewerProps> = ({
  formData,
  onClose,
  title,
}) => {
  const { t } = useTranslation(["forms", "common"]);
  const renderFormElement = (element: TaskFormElement, index: number) => {
    const getDefaultValue = () => {
      if (formData.submissionData && formData.submissionData[element.id]) {
        return formData.submissionData[element.id];
      }
      return element.value || element.placeholder || "";
    };

    const commonStyles =
      "w-full p-4 border border-gray-200 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 shadow-sm";

    switch (element.type) {
      case "text":
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={commonStyles}>
              {getDefaultValue() || (
                <span className="text-gray-400 italic">
                  {t("forms:noValueEntered")}
                </span>
              )}
            </div>
          </div>
        );

      case "textarea":
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div
              className={`${commonStyles} min-h-[120px] whitespace-pre-wrap`}
            >
              {getDefaultValue() || (
                <span className="text-gray-400 italic">
                  {t("forms:noValueEntered")}
                </span>
              )}
            </div>
          </div>
        );

      case "select": {
        const selectValue = getDefaultValue();
        const displayValue = Array.isArray(selectValue)
          ? selectValue.join(", ")
          : selectValue;
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={commonStyles}>
              {displayValue || (
                <span className="text-gray-400 italic">
                  {t("forms:noSelectionMade")}
                </span>
              )}
            </div>
            {element.options && element.options.length > 0 && (
              <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600 font-medium mb-1">
                  {t("forms:availableOptions")}:
                </p>
                <div className="flex flex-wrap gap-1">
                  {element.options.map((option, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-white text-xs text-gray-600 rounded border"
                    >
                      {option}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      case "checkbox":
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <div className="flex items-start space-x-4">
              <div className="flex items-center h-5 mt-1">
                <div
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all shadow-sm ${
                    getDefaultValue() === "true" || getDefaultValue() === true
                      ? "bg-gradient-to-r from-green-500 to-green-600 border-green-500 shadow-green-200"
                      : "border-gray-300 bg-gray-50"
                  }`}
                >
                  {(getDefaultValue() === "true" ||
                    getDefaultValue() === true) && (
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <label className="text-sm font-semibold text-gray-800">
                  {element.label}
                  {element.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {t("forms:status")}:{" "}
                  <span
                    className={`font-medium ${
                      getDefaultValue() === "true" || getDefaultValue() === true
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}
                  >
                    {getDefaultValue() === "true" || getDefaultValue() === true
                      ? t("forms:checked")
                      : t("forms:unchecked")}
                  </span>
                </p>
              </div>
            </div>
          </div>
        );

      case "date":
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`${commonStyles} flex items-center space-x-2`}>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {getDefaultValue() || (
                  <span className="text-gray-400 italic">
                    {t("forms:noDateSelected")}
                  </span>
                )}
              </span>
            </div>
          </div>
        );

      case "number":
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`${commonStyles} flex items-center space-x-2`}>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                />
              </svg>
              <span>
                {getDefaultValue() || (
                  <span className="text-gray-400 italic">
                    {t("forms:noValueEntered")}
                  </span>
                )}
              </span>
            </div>
          </div>
        );

      default:
        return (
          <div
            key={index}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              {element.label}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`${commonStyles} flex items-center space-x-2`}>
              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              <span>
                {getDefaultValue() || (
                  <span className="text-gray-400 italic">
                    {t("forms:noValue")}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              {t("forms:customFieldType")}: {element.type}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="flex-shrink-0 p-6 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <DocumentTextIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {title || formData.formName || t("forms:formPreview")}
              </h3>
              <p className="text-sm text-gray-500 flex items-center space-x-2 mt-1">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>{t("forms:readOnlyView")}</span>
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Form Description */}
      {formData.formDescription && (
        <div className="flex-shrink-0 p-6 bg-blue-50 border-b border-blue-100">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-200">
            <p className="text-gray-700 leading-relaxed">
              {formData.formDescription}
            </p>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {formData.elements && formData.elements.length > 0 ? (
          <div className="flex gap-6 max-w-7xl">
            {/* Left side - Form Elements */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8 space-y-6">
                  {formData.elements.map((element, index) =>
                    renderFormElement(element, index)
                  )}
                </div>
              </div>
            </div>

            {/* Right side - Form Info Panel */}
            <div className="w-80 flex-shrink-0">
              <div className="space-y-4">
                {/* Form Summary Card */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      {t("forms:formSummary")}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">
                        {t("forms:totalFields")}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formData.elements.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-600">
                        {t("forms:requiredFields")}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formData.elements.filter((el) => el.required).length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-600">
                        {t("forms:formType")}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {t("forms:interactive")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Field Types Card */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <svg
                        className="w-5 h-5 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      {t("forms:fieldTypes")}
                    </h3>
                  </div>

                  <div className="space-y-2">
                    {(() => {
                      const fieldTypes = formData.elements.reduce(
                        (acc: Record<string, number>, element) => {
                          acc[element.type] = (acc[element.type] || 0) + 1;
                          return acc;
                        },
                        {}
                      );

                      return Object.entries(fieldTypes).map(([type, count]) => (
                        <div
                          key={type}
                          className="flex justify-between items-center py-1"
                        >
                          <span className="text-sm capitalize text-gray-600">
                            {type.replace("_", " ")}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {count}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center max-w-md">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DocumentTextIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t("forms:emptyForm")}
              </h3>
              <p className="text-gray-500">{t("forms:emptyFormDescription")}</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200 shadow-sm">
        <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>
              {formData.elements?.length || 0}{" "}
              {(formData.elements?.length || 0) !== 1
                ? t("forms:fields")
                : t("forms:field")}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <DocumentTextIcon className="w-4 h-4" />
            <span>{t("forms:previewMode")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
